import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useGetSubscriptionsLookupQuery, useGetUsersInfiniteQuery } from 'src/apiClients';
import { USER_SEARCH_COLUMNS, USER_UUID_COLUMNS } from 'src/apiClients/queries';
import type { UserListItem } from 'src/apiClients/queries';
import {
    SortDirection,
    type DataTableColDef,
    type DataTableRow,
} from 'src/components/data-table/DataTable.types';
import { EntityListPage } from 'src/components/entity-list-page';
import { defaultFiltersFor } from 'src/filters/entity-filter-registry';
import { useSharedEntityFilters } from 'src/hooks/use-shared-entity-filters';
import { ROUTES } from 'src/utils/strings';
import { createNaFallback } from 'src/utils/table-formatters';

import { createUserColumns, mapUserRow } from './user-table-formatters';
import { userTableI18n } from './user-table-formatters.i18n';
import { i18n } from './UsersPage.i18n';

// `login`/`email`/`name` are text columns matched with `$iLike`. `id` and
// `seequentUserId` are UUID columns that can't use `$iLike` (Postgres type
// mismatch); they're matched exactly via `$eq` when the search term is a full
// UUID. This keeps a `?q=<uuid>` deep link (e.g. for a user with no login or
// email) working instead of erroring.
const SEARCH_COLUMNS = USER_SEARCH_COLUMNS;
const UUID_SEARCH_COLUMNS = USER_UUID_COLUMNS;
const INITIAL_SORT = [{ field: 'state', direction: SortDirection.ASC }];
const INITIAL_FILTERS = defaultFiltersFor('user');

export const UsersPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const initialSearch = searchParams.get('q') ?? '';
    const highlightId = (location.state as { highlightId?: string } | null)?.highlightId;

    const [filters, setFilters] = useSharedEntityFilters('user', INITIAL_FILTERS);

    // Small lookupfor customerName. Will be removed when backend embeds
    // subscription on user.
    const { data: subscriptions = [] } = useGetSubscriptionsLookupQuery();
    const customerNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const sub of subscriptions) map.set(sub.id, sub.customerName);
        return map;
    }, [subscriptions]);

    const mapRow = useCallback(
        (user: UserListItem): DataTableRow => ({
            ...mapUserRow(user),
            customerName: customerNameById.get(user.subscriptionId) ?? '',
            subscriptionId: user.subscriptionId,
        }),
        [customerNameById],
    );

    const columns = useMemo<DataTableColDef[]>(() => {
        const naFallback = createNaFallback(intl);
        const base = createUserColumns(intl);

        // Override login header label for top-level users page
        const loginCol = base.find((c) => c.field === 'login');
        if (loginCol) loginCol.headerName = intl.formatMessage(i18n.account);

        // Insert customerName after email
        const emailIdx = base.findIndex((c) => c.field === 'email');
        base.splice(emailIdx + 1, 0, {
            field: 'customerName',
            headerName: intl.formatMessage(i18n.customer),
            flex: 2,
            minWidth: 160,
            valueFormatter: naFallback,
            clientSort: true,
        });

        return base;
    }, [intl]);

    const handleRowClick = useCallback(
        (row: DataTableRow) => {
            const userId = row.id;
            if (typeof userId === 'string' && userId !== '') {
                navigate(`${ROUTES.USERS}/${userId}`);
            }
        },
        [navigate],
    );

    return (
        <EntityListPage<UserListItem>
            // Remount when the `?q=` deep link changes so the seeded search resets.
            key={initialSearch}
            automationId="users-page"
            useQuery={useGetUsersInfiniteQuery}
            searchColumns={SEARCH_COLUMNS}
            uuidSearchColumns={UUID_SEARCH_COLUMNS}
            initialSort={INITIAL_SORT}
            initialSearch={initialSearch}
            columns={columns}
            mapRow={mapRow}
            onRowClick={handleRowClick}
            highlightedRowId={highlightId}
            initialFilters={filters}
            onFiltersChange={setFilters}
            titleWhenNoResults={intl.formatMessage(userTableI18n.noUsersTitle)}
            messageWhenNoResults={intl.formatMessage(i18n.noUsersMessage)}
            titleWhenLoadError={intl.formatMessage(userTableI18n.loadErrorTitle)}
            messageWhenLoadError={intl.formatMessage(userTableI18n.loadErrorMessage)}
        />
    );
};
